import type { ReactElement } from "react"

import { useTranslation } from "react-i18next"

import CodeIcon from "@mui/icons-material/Code"
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined"
import { RoundedTab, Tabs } from "@synapse-analytics/synapse-ui"

import styles from "./TableJsonSwitch.module.scss"

type TableJsonSwitchProps = {
  isJsonMode: boolean
  handleSwitchMode: () => void
}

/**
 * A component that provides a toggle switch between JSON and Table view modes.
 * It displays two tabs: one for JSON mode and one for Table mode, allowing users to switch between them.
 *
 * @param {boolean} isJsonMode - Indicates if the current mode is JSON.
 * @param {function} handleSwitchMode - Function to handle switching between JSON and Table modes.
 * @returns {ReactElement} A Tabs component with two RoundedTab components for switching modes.
 */
export function TableJsonSwitch(props: Readonly<TableJsonSwitchProps>): ReactElement {
  const { isJsonMode, handleSwitchMode } = props

  const { t } = useTranslation("UI")

  return (
    <Tabs activeTab={isJsonMode ? "JSON" : "Table"} className={styles.tabsContainer}>
      <RoundedTab
        value={"JSON"}
        label={t("TableJsonSwitch.json")}
        icon={() => <CodeIcon style={{ width: "16px", height: "16px" }} />}
        size="default"
        selected={isJsonMode}
        onClick={handleSwitchMode}
      />
      <RoundedTab
        value={"Table"}
        label={t("TableJsonSwitch.table")}
        icon={() => <TableRowsOutlinedIcon style={{ width: "16px", height: "16px", rotate: "90deg" }} />}
        size="default"
        selected={!isJsonMode}
        onClick={handleSwitchMode}
      />
    </Tabs>
  )
}
